import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { apiClient } from "../../../apiclient/apiclient";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";

const inputClass =
  "border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full";

const labelClass = "text-xs font-medium text-slate-700";

const POSCheckoutModal = ({ isOpen, onClose, checkoutMode, clients, onCheckout, onAddClient }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
  });

  const [matchedClient, setMatchedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset modal state when opening
  useEffect(() => {
    if (isOpen) {
      setFormData({ name: "", phone: "", address: "" });
      setMatchedClient(null);
      setErrors({});
    }
  }, [isOpen]);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Digits only
    if (value.length > 10) return;

    setFormData((prev) => ({ ...prev, phone: value }));
    if (errors.phone) setErrors((prev) => ({ ...prev, phone: "" }));

    if (value.length === 10) {
      const matched = clients.find((c) => c.phone === value);
      if (matched) {
        setMatchedClient(matched);
        setFormData((prev) => ({
          ...prev,
          name: matched.name || "",
          address: matched.address || "",
        }));
      } else {
        setMatchedClient(null);
        setFormData((prev) => ({ ...prev, name: "", address: "" }));
      }
    } else {
      // If phone length backspaces from 10, clear the match and input values
      if (matchedClient) {
        setMatchedClient(null);
        setFormData((prev) => ({ ...prev, name: "", address: "" }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.phone.trim()) {
      newErrors.phone = "Mobile number is required";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Enter a valid 10-digit mobile number";
    }

    if (!matchedClient && !formData.name.trim()) {
      newErrors.name = "Client name is required for new registration";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (matchedClient) {
      onCheckout(matchedClient._id);
      onClose();
      return;
    }

    const newErrors = validateForm();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post("/clients", {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        clientType: "pos",
      });

      const newClient = res.data?.client;
      toast.success("Client registered successfully");
      
      // Pass the new client's ID and execute checkout callback
      onCheckout(newClient._id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save client details");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onCheckout(null); // Checkout as anonymous walk-in
    onClose();
  };

  if (!isOpen) return null;

  const isCreditMode = checkoutMode === "credit";

  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-2">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-lg w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* HEADER */}
        <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
          <div>
            <h2 className="text-lg font-semibold">Checkout - Customer Info</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] opacity-85 uppercase tracking-wider font-semibold">
                Mode: {isCreditMode ? "Pay Later" : "Pay Now"}
              </span>
              {formData.phone.length === 10 && (
                <>
                  <span className="text-[10px] opacity-60">•</span>
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${matchedClient ? "bg-emerald-600 text-white" : "bg-orange-500 text-white"}`}>
                    {matchedClient ? "Existing Customer" : "New Customer"}
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xl text-white font-normal hover:opacity-85 transition-opacity"
          >
            ✕
          </button>
        </div>

        {/* FORM BODY */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
          {isCreditMode && (
            <p className="text-[11px] text-slate-500 leading-relaxed">
              💡 Customer details are required for Credit/Pay Later orders to record balance.
            </p>
          )}

          {/* Phone */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Mobile Number *</label>
            <input
              type="text"
              name="phone"
              maxLength="10"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="Enter 10-digit mobile number"
              className={inputClass}
            />
            {errors.phone && <p className="text-red-500 text-xs mt-0.5">{errors.phone}</p>}
          </div>

          {formData.phone.length === 10 && (
            <div className="flex flex-col gap-4 transition-all duration-200">

              {/* Name */}
              <div className="flex flex-col gap-1">
                <label className={labelClass}>Customer Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. John Doe"
                  className={`${inputClass} ${matchedClient ? "bg-slate-100 text-slate-600 cursor-not-allowed border-slate-200" : ""}`}
                  disabled={!!matchedClient}
                />
                {errors.name && <p className="text-red-500 text-xs mt-0.5">{errors.name}</p>}
              </div>

              {/* Address */}
              <div className="flex flex-col gap-1">
                <label className={labelClass}>Address</label>
                <textarea
                  name="address"
                  rows="2"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Billing/Delivery Address"
                  className={`${inputClass} resize-none ${matchedClient ? "bg-slate-100 text-slate-600 cursor-not-allowed border-slate-200" : ""}`}
                  disabled={!!matchedClient}
                />
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t bg-slate-50 flex justify-between items-center gap-2">
          <div>
            {!isCreditMode && (
              <button
                type="button"
                onClick={handleSkip}
                className="bg-white hover:bg-slate-100 text-slate-600 font-semibold px-4 py-2 rounded-md transition-colors text-sm border border-slate-200"
              >
                Skip & Checkout
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-white hover:bg-slate-100 text-slate-700 font-semibold px-4 py-2 rounded-md transition-colors text-sm border border-slate-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (formData.phone.length !== 10)}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader text="Saving..." inline /> : "Proceed"}
            </button>
          </div>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default POSCheckoutModal;
